import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { gamePackAPI } from '../api';

const CATEGORIES = [
  {
    id: 'otazky',
    label: 'Otázky',
    color: 'blue',
    modes: [
      { id: 'abcd', name: 'A/B/C/D', description: 'Výběr ze čtyř možností', icon: '🔡' },
      { id: 'true-false', name: 'Pravda / Nepravda', description: 'Žák rozhodne: pravda nebo nepravda', icon: '✅' },
      { id: 'text-answer', name: 'Textová odpověď', description: 'Žák napíše krátkou odpověď', icon: '📝' },
      { id: 'sort-options', name: 'Řazení možností', description: 'Žáci seřadí rozházené možnosti správně', icon: '🔢' },
      { id: 'fill-sentence', name: 'Doplnění slova do věty', description: 'Žáci doplní zakrytá slova do textu', icon: '✏️' },
    ],
  },
  {
    id: 'kresleni',
    label: 'Kreslení',
    color: 'purple',
    modes: [
      { id: 'teacher-draw', name: 'Kresba ze zadání', description: 'Učitel zadá, co žáci mají nakreslit', icon: '✍️' },
      { id: 'draw', name: 'Interaktivní kresba', description: 'Žáci si navzájem dají zadání a kreslí', icon: '🎨' },
      { id: 'draw-guess', name: 'Kresba s hádáním', description: 'Žáci píší zadání → kreslí → tipují původní zadání', icon: '🎭' },
    ],
  },
  {
    id: 'programovani',
    label: 'Programování',
    color: 'green',
    modes: [
      { id: 'explain-it', name: 'Vysvětlení', description: 'Tvůrce napíše kód, řešitel vysvětlí jeho funkci', icon: '💬' },
      { id: 'verbal-to-code', name: 'Coding dle zadání', description: 'Tvůrce popíše úkol slovy, řešitel naprogramuje', icon: '⌨️' },
      { id: 'fill-in-code', name: 'Doplň kód', description: 'Učitel zadá kód s mezerou, žáci doplní chybějící část', icon: '✒️' },
      { id: 'click-the-bug', name: 'Hledání chyby', description: 'Učitel zadá kód s chybou, žáci kliknou na chybný řádek', icon: '🎯' },
    ],
  },
];

const ALL_MODES = CATEGORIES.flatMap(c => c.modes);

const SOLO_TYPES = [
  'abcd', 'true-false', 'text-answer', 'sort-options', 'fill-sentence',
  'teacher-draw', 'fill-in-code', 'click-the-bug',
];

const categoryColorMap = {
  blue: {
    section: 'text-blue-700',
    badge: 'bg-blue-100 text-blue-700',
    selected: 'border-blue-500 bg-blue-50',
    hover: 'hover:border-blue-300',
  },
  purple: {
    section: 'text-purple-700',
    badge: 'bg-purple-100 text-purple-700',
    selected: 'border-purple-500 bg-purple-50',
    hover: 'hover:border-purple-300',
  },
  green: {
    section: 'text-green-700',
    badge: 'bg-green-100 text-green-700',
    selected: 'border-green-500 bg-green-50',
    hover: 'hover:border-green-300',
  },
};

const getCategory = (typeId) => CATEGORIES.find(c => c.modes.some(m => m.id === typeId));
const getModeInfo = (typeId) => ALL_MODES.find(m => m.id === typeId);

const defaultRound = () => ({
  type: '',
  isSoloRound: false,
  solutionTimeLimit: 60,
  creationTimeLimit: 180,
  guessTimeLimit: 60,
  pointsPerCorrect: 1,
  taskDescription: '',
  question: '',
  statement: '',
  correctAnswer: '',
  options: ['', '', '', ''],
  sortItems: ['', '', ''],
  sentenceTemplate: '',
  drawPrompt: '',
  codeTemplate: '',
  correctFill: '',
  buggyCode: '',
  bugLine: 1,
  bugExplanation: '',
});

const GamePackBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rounds, setRounds] = useState([]);
  const [typePickerOpen, setTypePickerOpen] = useState([]);

  useEffect(() => {
    if (id) loadGamePack();
  }, [id]);

  const loadGamePack = async () => {
    setLoading(true);
    try {
      const response = await gamePackAPI.getOne(id);
      const pack = response.data;
      setName(pack.name);
      setDescription(pack.description);
      const r = pack.rounds || [];
      setRounds(r);
      setTypePickerOpen(r.map(() => false));
    } catch (error) {
      alert('Nepodařilo se načíst herní balíček');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const addRound = () => {
    setRounds(prev => [...prev, defaultRound()]);
    setTypePickerOpen(prev => [...prev, true]);
  };

  const removeRound = (index) => {
    setRounds(prev => prev.filter((_, i) => i !== index));
    setTypePickerOpen(prev => prev.filter((_, i) => i !== index));
  };

  const moveRound = (index, direction) => {
    const target = direction === 'up' ? index - 1 : index + 1;
    const newRounds = [...rounds];
    const newOpen = [...typePickerOpen];
    [newRounds[index], newRounds[target]] = [newRounds[target], newRounds[index]];
    [newOpen[index], newOpen[target]] = [newOpen[target], newOpen[index]];
    setRounds(newRounds);
    setTypePickerOpen(newOpen);
  };

  const updateRound = (index, field, value) => {
    const newRounds = [...rounds];
    newRounds[index] = { ...newRounds[index], [field]: value };
    setRounds(newRounds);
  };

  const selectType = (index, typeId) => {
    const newRounds = [...rounds];
    newRounds[index] = {
      ...newRounds[index],
      type: typeId,
      isSoloRound: SOLO_TYPES.includes(typeId),
    };
    setRounds(newRounds);
    const newOpen = [...typePickerOpen];
    newOpen[index] = false;
    setTypePickerOpen(newOpen);
  };

  const openTypePicker = (index) => {
    const newOpen = [...typePickerOpen];
    newOpen[index] = true;
    setTypePickerOpen(newOpen);
  };

  const handleSave = async () => {
    if (!name.trim()) { alert('Zadejte název herního balíčku'); return; }
    if (rounds.length === 0) { alert('Přidejte alespoň jedno kolo'); return; }
    for (let i = 0; i < rounds.length; i++) {
      if (!rounds[i].type) { alert(`Kolo ${i + 1}: vyberte typ`); return; }
    }
    setSaving(true);
    try {
      if (id) {
        await gamePackAPI.update(id, { name, description, rounds });
      } else {
        await gamePackAPI.create({ name, description, rounds });
      }
      navigate('/dashboard');
    } catch (error) {
      alert('Nepodařilo se uložit herní balíček');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800">
              {id ? 'Upravit herní balíček' : 'Nový herní balíček'}
            </h1>
            <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-800">
              ← Zpět
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Název balíčku *</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Můj úžasný herní balíček"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Popis (volitelný)</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={2}
                placeholder="Stručný popis herního balíčku..."
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Kola ({rounds.length})</h2>
            <button onClick={addRound} className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors">
              ➕ Přidat kolo
            </button>
          </div>

          {rounds.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">🎮</div>
              <p className="text-xl">Zatím nemáte žádná kola</p>
              <p className="mt-2">Přidejte první kolo kliknutím na tlačítko výše</p>
            </div>
          ) : (
            <div className="space-y-4">
              {rounds.map((round, index) => (
                <RoundCard
                  key={index}
                  round={round}
                  index={index}
                  total={rounds.length}
                  pickerOpen={typePickerOpen[index] ?? true}
                  onSelectType={selectType}
                  onOpenPicker={openTypePicker}
                  onUpdate={updateRound}
                  onRemove={removeRound}
                  onMove={moveRound}
                />
              ))}
              <div className="pt-2">
                <button
                  onClick={addRound}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-green-400 hover:text-green-600 transition-colors font-semibold"
                >
                  ➕ Přidat další kolo
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-primary-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {saving ? 'Ukládání...' : 'Uložit herní balíček'}
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-8 py-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Zrušit
          </button>
        </div>
      </div>
    </div>
  );
};

const RoundCard = ({ round, index, total, pickerOpen, onSelectType, onOpenPicker, onUpdate, onRemove, onMove }) => {
  const modeInfo = getModeInfo(round.type);
  const category = getCategory(round.type);
  const colorKey = category?.color || 'blue';
  const colors = categoryColorMap[colorKey];

  return (
    <div className="border border-gray-200 rounded-xl bg-gray-50 overflow-hidden">
      <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-gray-700">Kolo {index + 1}</span>
          {modeInfo && !pickerOpen && (
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${colors?.badge}`}>
              {modeInfo.icon} {modeInfo.name}
            </span>
          )}
        </div>
        <div className="flex gap-1 items-center">
          {!pickerOpen && modeInfo && (
            <button
              onClick={() => onOpenPicker(index)}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-100 transition-colors mr-2"
            >
              Změnit typ úkolu
            </button>
          )}
          {index > 0 && (
            <button onClick={() => onMove(index, 'up')} className="text-gray-500 hover:text-gray-700 p-1" title="Posunout nahoru">⬆️</button>
          )}
          {index < total - 1 && (
            <button onClick={() => onMove(index, 'down')} className="text-gray-500 hover:text-gray-700 p-1" title="Posunout dolů">⬇️</button>
          )}
          <button onClick={() => onRemove(index)} className="text-red-500 hover:text-red-700 p-1" title="Odstranit">🗑️</button>
        </div>
      </div>

      <div className="p-6">
        {pickerOpen ? (
          <TypePicker round={round} index={index} onSelectType={onSelectType} />
        ) : (
          <RoundFields round={round} index={index} onUpdate={onUpdate} />
        )}
      </div>
    </div>
  );
};

const TypePicker = ({ round, index, onSelectType }) => (
  <div className="space-y-6">
    {CATEGORIES.map(cat => {
      const colors = categoryColorMap[cat.color];
      return (
        <div key={cat.id}>
          <div className={`text-xs font-bold uppercase tracking-widest mb-3 ${colors.section}`}>
            {cat.label}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {cat.modes.map(mode => (
              <button
                key={mode.id}
                onClick={() => onSelectType(index, mode.id)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  round.type === mode.id ? colors.selected : `border-gray-200 ${colors.hover}`
                }`}
              >
                <div className="text-2xl mb-2">{mode.icon}</div>
                <div className="font-semibold text-gray-800 mb-1">{mode.name}</div>
                <div className="text-xs text-gray-500">{mode.description}</div>
              </button>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

const RoundFields = ({ round, index, onUpdate }) => {
  const u = (field, value) => onUpdate(index, field, value);
  const isSolo = SOLO_TYPES.includes(round.type);
  const isProg = ['explain-it', 'verbal-to-code', 'fill-in-code', 'click-the-bug'].includes(round.type);

  const [timeRaw, setTimeRaw] = useState({
    creationTimeLimit: String(round.creationTimeLimit ?? 180),
    solutionTimeLimit: String(round.solutionTimeLimit ?? 60),
    guessTimeLimit: String(round.guessTimeLimit ?? 60),
    pointsPerCorrect: String(round.pointsPerCorrect ?? 1),
  });

  const handleTimeChange = (field, raw) => {
    setTimeRaw(prev => ({ ...prev, [field]: raw }));
    const num = parseInt(raw, 10);
    if (!isNaN(num)) u(field, num);
  };

  const handleTimeBlur = (field, defaultVal) => {
    const num = parseInt(timeRaw[field], 10);
    if (isNaN(num) || timeRaw[field].trim() === '') {
      setTimeRaw(prev => ({ ...prev, [field]: String(defaultVal) }));
      u(field, defaultVal);
    }
  };

  const blanksFromTemplate = () => {
    const template = round.sentenceTemplate || '';
    const result = [];
    const regex = /\[\[(.*?)\]\]/g;
    let m;
    while ((m = regex.exec(template)) !== null) result.push(m[1]);
    return result;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {!isSolo && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Čas na tvorbu (s)</label>
            <input type="number" value={timeRaw.creationTimeLimit} min={30} max={1800}
              onChange={e => handleTimeChange('creationTimeLimit', e.target.value)}
              onBlur={() => handleTimeBlur('creationTimeLimit', 180)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isSolo ? 'Čas na odpověď (s)' : 'Čas na řešení (s)'}
          </label>
          <input type="number" value={timeRaw.solutionTimeLimit} min={10} max={1800}
            onChange={e => handleTimeChange('solutionTimeLimit', e.target.value)}
            onBlur={() => handleTimeBlur('solutionTimeLimit', 60)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
        </div>
        {round.type === 'draw-guess' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Čas na tipování (s)</label>
            <input type="number" value={timeRaw.guessTimeLimit} min={10} max={600}
              onChange={e => handleTimeChange('guessTimeLimit', e.target.value)}
              onBlur={() => handleTimeBlur('guessTimeLimit', 60)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
          </div>
        )}
        {isSolo && round.type !== 'teacher-draw' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body za správnou odpověď</label>
            <input type="number" value={timeRaw.pointsPerCorrect} min={1} max={10}
              onChange={e => handleTimeChange('pointsPerCorrect', e.target.value)}
              onBlur={() => handleTimeBlur('pointsPerCorrect', 1)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
          </div>
        )}
      </div>

      {isProg && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Doplňující pokyn (volitelný)</label>
          <textarea value={round.taskDescription || ''} onChange={e => u('taskDescription', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            rows={2} placeholder="Např. 'Použijte cyklus for' nebo 'Pište v C#'..." />
        </div>
      )}

      {round.type === 'abcd' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Otázka *</label>
            <textarea value={round.question || ''} onChange={e => u('question', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={2} placeholder="Napiš otázku..." />
          </div>
          <div className="space-y-2">
            {['A', 'B', 'C', 'D'].map((letter, oi) => (
              <div key={letter} className="flex items-center gap-2">
                <span className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 transition-colors ${round.correctAnswer === letter ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}>
                  {letter}
                </span>
                <input type="text" value={(round.options || [])[oi] || ''}
                  onChange={e => { const o = [...(round.options || ['','','',''])]; o[oi] = e.target.value; u('options', o); }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder={`Možnost ${letter}`} />
                <button onClick={() => u('correctAnswer', letter)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all ${round.correctAnswer === letter ? 'bg-green-500 text-white' : 'border border-gray-300 text-gray-500 hover:border-green-400'}`}>
                  ✓
                </button>
              </div>
            ))}
          </div>
          {!round.correctAnswer && <p className="text-sm text-amber-600">Označte správnou odpověď kliknutím na ✓</p>}
        </>
      )}

      {round.type === 'true-false' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tvrzení *</label>
            <textarea value={round.statement || ''} onChange={e => u('statement', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={2} placeholder="Napiš tvrzení, které žáci zhodnotí..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Správná odpověď *</label>
            <div className="flex gap-3">
              <button onClick={() => u('correctAnswer', 'true')}
                className={`flex-1 py-3 rounded-xl border-2 font-bold text-lg transition-all ${round.correctAnswer === 'true' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-green-400'}`}>
                ✅ Pravda
              </button>
              <button onClick={() => u('correctAnswer', 'false')}
                className={`flex-1 py-3 rounded-xl border-2 font-bold text-lg transition-all ${round.correctAnswer === 'false' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 hover:border-red-400'}`}>
                ❌ Nepravda
              </button>
            </div>
          </div>
        </>
      )}

      {round.type === 'text-answer' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Otázka *</label>
            <textarea value={round.question || ''} onChange={e => u('question', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={2} placeholder="Napiš otázku..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Správná odpověď *</label>
            <input type="text" value={round.correctAnswer || ''} onChange={e => u('correctAnswer', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Správná odpověď (porovnává se bez ohledu na velikost písmen)" />
          </div>
        </>
      )}

      {round.type === 'sort-options' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Otázka / Zadání (volitelné)</label>
            <textarea value={round.question || ''} onChange={e => u('question', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={2} placeholder="Co mají žáci seřadit?" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Možnosti ve správném pořadí</label>
            <div className="space-y-2">
              {(round.sortItems || ['', '', '']).map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {i + 1}
                  </span>
                  <input type="text" value={item}
                    onChange={e => { const it = [...(round.sortItems || [])]; it[i] = e.target.value; u('sortItems', it); }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder={`Položka ${i + 1}`} />
                  {i >= 2 && (
                    <button onClick={() => { const it = [...(round.sortItems || [])]; it.splice(i, 1); u('sortItems', it); }}
                      className="text-red-400 hover:text-red-600 px-2 text-lg">✖</button>
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => u('sortItems', [...(round.sortItems || []), ''])}
              className="mt-2 text-sm text-primary-600 hover:text-primary-800 font-semibold">
              + Přidat položku
            </button>
          </div>
        </>
      )}

      {round.type === 'fill-sentence' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Text s označenými slovy *</label>
            <p className="text-xs text-gray-500 mb-2">
              Slova/fráze k doplnění obalte do <code className="bg-gray-100 px-1 rounded">{'[[...'}</code><code className="bg-gray-100 px-1 rounded">{']]'}</code>.
              Příklad: <span className="font-mono text-xs bg-yellow-50 border border-yellow-200 px-1 rounded">Hlavní město Francie je {'[['}Paříž{']]'}.</span>
            </p>
            <textarea value={round.sentenceTemplate || ''} onChange={e => u('sentenceTemplate', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={4} placeholder={'Hlavní město ČR je [[Praha]] a leží na řece [[Vltavě]].'} />
          </div>
          {blanksFromTemplate().length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-yellow-700 mb-2">
                Detekované odpovědi ({blanksFromTemplate().length}):
              </div>
              <div className="flex flex-wrap gap-2">
                {blanksFromTemplate().map((b, i) => (
                  <span key={i} className="bg-white border border-yellow-300 rounded px-2 py-1 text-sm font-mono text-gray-800">
                    {i + 1}. {b}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {round.type === 'teacher-draw' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Zadání pro kreslení *</label>
          <textarea value={round.drawPrompt || ''} onChange={e => u('drawPrompt', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            rows={2} placeholder="Nakreslete domeček se zahradou a sluncem..." />
        </div>
      )}

      {(round.type === 'draw' || round.type === 'draw-guess') && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm text-purple-700">
          {round.type === 'draw'
            ? '🎨 Žáci si navzájem dají zadání, co nakreslit. Není třeba nastavení od učitele.'
            : '🎭 Žáci napíší zadání → jiný žák kreslí → původní autor tipuje co bylo nakresleno. Bez nastavení učitele.'}
        </div>
      )}

      {round.type === 'explain-it' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
          💬 Žáci napíší 3–5 řádků kódu, ostatní vysvětlí funkci.
        </div>
      )}

      {round.type === 'verbal-to-code' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
          ⌨️ Žáci popíší programátorský úkol slovy, ostatní ho naprogramují.
        </div>
      )}

      {round.type === 'fill-in-code' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kód s mezerou (použijte <code className="bg-gray-100 px-1 rounded">___</code>) *
            </label>
            <textarea value={round.codeTemplate || ''} onChange={e => u('codeTemplate', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono"
              rows={6} placeholder={'Console.___(\"Ahoj\");'} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Správná odpověď (co patří místo ___) *</label>
            <input type="text" value={round.correctFill || ''} onChange={e => u('correctFill', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono"
              placeholder="WriteLine" />
          </div>
        </>
      )}

      {round.type === 'click-the-bug' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kód s chybou *</label>
            <textarea value={round.buggyCode || ''} onChange={e => u('buggyCode', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono"
              rows={8} placeholder={'int x = 10;\nint y = 0;\nint z = x / y;\nConsole.WriteLine(z);'} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Číslo řádku s chybou *</label>
              <input type="number" value={round.bugLine || 1} min={1}
                onChange={e => u('bugLine', parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vysvětlení chyby (volitelné)</label>
              <input type="text" value={round.bugExplanation || ''} onChange={e => u('bugExplanation', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Dělení nulou" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GamePackBuilder;
